// BlueprintLotteryUI.js - 图纸/抽奖 NPC 界面（与商店布局一致）

import BackgroundHelper from '../BackgroundHelper.js';
import UIStyleHelper from '../UIStyleHelper.js';

export default class BlueprintLotteryUI {
    constructor(scene) {
        this.scene = scene;
        this.bg = null;
        this.title = null;
        this.moneyText = null;
        this.ticketText = null;
        this.itemList = null;
        this.itemDetails = null;
        this.helpText = null;
        this.modeText = null;
        this.messageText = null;
        this.listPanel = null;
        this.detailPanel = null;
    }

    create(npcName) {
        const { width, height, centerX } = this.scene.cameras.main;
        const panelTop = height * 0.16;
        const panelHeight = height * 0.56;
        const leftPanelW = Math.min(260, width * 0.24);
        const leftPanelX = width * 0.1;
        const rightPanelW = width * 0.54;
        const rightPanelX = leftPanelX + leftPanelW + width * 0.04;

        this.bg = BackgroundHelper.createSemiTransparentBackground(this.scene, 0.85);

        this.title = UIStyleHelper.createSubtitle(
            this.scene,
            `${npcName} · 图纸与抽奖`,
            centerX,
            28,
            { fill: '#ffcc00' }
        );

        this.moneyText = UIStyleHelper.createMoneyText(this.scene, 0, width * 0.08, 28);
        this.ticketText = UIStyleHelper.createText(
            this.scene,
            '抽奖券: 0',
            width * 0.92,
            28,
            { fill: '#4cc9f0', fontSize: '16px' }
        );
        this.ticketText.setOrigin(1, 0.5);

        this.listPanel = this.scene.add.graphics();
        this.listPanel.fillStyle(0x12122a, 0.9);
        this.listPanel.fillRoundedRect(leftPanelX, panelTop, leftPanelW, panelHeight, 8);
        this.listPanel.lineStyle(2, 0x4cc9f0, 0.6);
        this.listPanel.strokeRoundedRect(leftPanelX, panelTop, leftPanelW, panelHeight, 8);
        this.listPanel.setScrollFactor(0);
        this.listPanel.setDepth(10);

        this.detailPanel = this.scene.add.graphics();
        this.detailPanel.fillStyle(0x12122a, 0.9);
        this.detailPanel.fillRoundedRect(rightPanelX, panelTop, rightPanelW, panelHeight, 8);
        this.detailPanel.lineStyle(2, 0xffaa00, 0.5);
        this.detailPanel.strokeRoundedRect(rightPanelX, panelTop, rightPanelW, panelHeight, 8);
        this.detailPanel.setScrollFactor(0);
        this.detailPanel.setDepth(10);

        this.itemList = this.scene.add.container(leftPanelX + leftPanelW / 2, panelTop + 48);
        this.itemList.setDepth(20);
        this.listBaseY = panelTop + 48;
        this.leftPanelWidth = leftPanelW - 20;
        this.listViewportHeight = panelHeight - 52;

        const listMaskGfx = this.scene.add.graphics();
        listMaskGfx.fillStyle(0xffffff);
        listMaskGfx.fillRect(leftPanelX + 6, panelTop + 40, leftPanelW - 12, panelHeight - 48);
        listMaskGfx.setVisible(false);
        this.itemList.setMask(listMaskGfx.createGeometryMask());
        this.listMaskGfx = listMaskGfx;

        this.itemDetails = this.scene.add.container(rightPanelX + rightPanelW / 2, panelTop + panelHeight * 0.42);
        this.itemDetails.setDepth(20);

        this.modeText = UIStyleHelper.createText(
            this.scene,
            '请选择操作',
            centerX,
            height * 0.1,
            { fill: '#ffff00', fontSize: '15px', fontStyle: 'bold' }
        );
        this.modeText.setOrigin(0.5);

        this.helpText = UIStyleHelper.createHint(
            this.scene,
            'W/S 移动 · 空格 确认 · ESC 返回',
            height - 36,
            { fontSize: '13px' }
        );
    }

    updateMoney(money) {
        if (this.moneyText) {
            this.moneyText.setText(`金钱: ${money} G`);
        }
    }

    updateTicketCount(count) {
        if (this.ticketText) {
            this.ticketText.setText(`抽奖券: ${count}`);
        }
    }

    updateModeText(mode) {
        const labels = {
            menu: '请选择操作',
            blueprint: '购买图纸',
            lottery: '抽奖服务'
        };
        if (this.modeText) {
            this.modeText.setText(labels[mode] || '');
        }
    }

    showMessage(text, color = '#ffffff') {
        if (this.messageText) {
            this.messageText.destroy();
        }

        this.messageText = this.scene.add.text(
            this.scene.cameras.main.centerX,
            this.scene.cameras.main.height - 72,
            text,
            {
                fontSize: '15px',
                fill: color,
                fontFamily: UIStyleHelper.PIXEL_FONT,
                align: 'center',
                backgroundColor: '#000000',
                padding: { x: 12, y: 6 },
                wordWrap: { width: 420 }
            }
        ).setOrigin(0.5);
        this.messageText.setScrollFactor(0);
        this.messageText.setDepth(100);

        this.scene.time.delayedCall(2800, () => {
            if (this.messageText) {
                this.messageText.destroy();
                this.messageText = null;
            }
        });
    }

    clearLists() {
        this.itemList?.removeAll(true);
        this.itemDetails?.removeAll(true);
        if (this.itemList && this.listBaseY != null) {
            this.itemList.y = this.listBaseY;
        }
    }

    scrollListToIndex(selectedIndex, itemCount, lineHeight) {
        if (!this.itemList || this.listViewportHeight == null) return;

        const totalHeight = itemCount * lineHeight;
        if (totalHeight <= this.listViewportHeight) {
            this.itemList.y = this.listBaseY;
            return;
        }

        const focusY = selectedIndex * lineHeight + lineHeight / 2;
        const scrollOffset = Phaser.Math.Clamp(
            focusY - this.listViewportHeight / 2,
            0,
            totalHeight - this.listViewportHeight
        );
        this.itemList.y = this.listBaseY - scrollOffset;
    }

    scrollListByItem(selectedTop, selectedHeight, totalHeight) {
        if (!this.itemList || this.listViewportHeight == null) return;

        if (totalHeight <= this.listViewportHeight) {
            this.itemList.y = this.listBaseY;
            return;
        }

        const focusY = selectedTop + selectedHeight / 2;
        const scrollOffset = Phaser.Math.Clamp(
            focusY - this.listViewportHeight / 2,
            0,
            totalHeight - this.listViewportHeight
        );
        this.itemList.y = this.listBaseY - scrollOffset;
    }
}
